customElements.define(
  "otp-input",
  class extends HTMLElement {
    static formAssociated = true;
    #internals;
    #fieldName;
    #otp = "";

    constructor() {
      
      const styles = document.querySelectorAll("style");
      const links = document.querySelectorAll('link[rel="stylesheet"]')
      const css = [...styles, ...links].map(el => el.outerHTML).join("\n");

      super();

      this.tabIndex = 0;

      this.#internals = this.attachInternals();
      this.#fieldName = this.getAttribute("name");

      const size = parseInt(this.getAttribute("size") || "5");
      const autofocus = !!this.getAttribute("autofocus");
      const inputTpl = `<input class='digit h4 text-center' type='text' inputmode='numeric' maxlength='1' />`;
      const inputs = [...Array(size).fill(inputTpl)];
      const shadowRoot = this.attachShadow({ mode: "open" });

      shadowRoot.innerHTML = `
        ${css}
        <style>
          .otp-input { justify-content: space-between; column-gap: 10px; }
          .digit { height: 3rem; }
        </style>
        <div class='otp-input flex-container flex-dir-row'>
          ${inputs.join("")}
        </div>
      `;
      this.#internals.setFormValue(this.#otp);

      shadowRoot.querySelectorAll("input.digit").forEach((input) => {
        input.addEventListener("keydown", (ev) => this.#handleKeydown(ev));
        input.addEventListener("paste", (ev) => this.#handlePaste(ev));
      });

      if (autofocus) {
        shadowRoot.querySelector("input.digit").focus();
      }

      this.checkValidity();
    }

    connectedCallback() {
      this.setAttribute("role", "input");
    }

    focus() {
      this.shadowRoot.querySelector("input.digit")?.focus();
    }

    get validity() {
      return this.#internals.validity;
    }

    get validationMessage() {
      return this.#internals.validationMessage;
    }

    get willValidate() {
      return this.#internals.willValidate;
    }

    setValidity(validity, message) {
      this.#internals.setValidity(validity, message);
    }

    #getValidationMessage() {
      const browserLang = navigator.language || 'en';
      const messages = {
        en: 'Please complete the OTP code',
        es: 'Completa el código OTP',
        fr: 'Veuillez compléter le code OTP',
        de: 'Bitte vervollständigen Sie den OTP-Code',
        it: 'Completa il codice OTP'
      };
      
      // Check language variations
      const langPrefix = browserLang.split('-')[0].toLowerCase();
      return messages[langPrefix] || messages.en;
    }

    checkValidity() {
      const required = this.getAttribute("required");
      const size = parseInt(this.getAttribute("size") || "5");
      const isValid = !required || this.value.length === size;
      
      if (!isValid) {
        this.#internals.setValidity(
          { customError: true }, 
          this.#getValidationMessage()  // Use localized message
        );
      } else {
        this.setValidity({});
      }
      
      return isValid;
    }

    reportValidity() {
      this.checkValidity();
      return this.#internals.reportValidity();
    }

    #handleKeydown(ev) {
      const { target, code, key, ctrlKey } = ev;
      const input = target;
      let prev, next;
      if (input.previousElementSibling?.nodeName == "INPUT") prev = input.previousElementSibling;
      if (input.nextElementSibling?.nodeName == "INPUT") next = input.nextElementSibling;
      if (code == "Backspace") {
        if (input.value == "" && prev) prev.value = "";
        input.value = "";
        prev?.focus();
      } else if (key.match(/[0-9]/i) ) {
        input.value = key;
        next?.focus();
      } else if (code == "ArrowLeft") {
        prev?.focus();
      } else if (code == "ArrowRight" && input.value != "") {
        next?.focus();
      } else if (code == "Enter") {
        this.form?.requestSubmit();
        ev.preventDefault();
        return;
      } else if (code == "F5" || ctrlKey) {
        return;
      }
      const inputs = Array.from(input.parentNode?.querySelectorAll("input") || []);
      this.value = inputs
        .map((i) => i.value)
        .filter((i) => !!i)
        .join("");
      ev.preventDefault();
    }

    #handlePaste(ev) {
      const { target, code, ctrlKey } = ev;
      const input = target;
      ev.preventDefault();
      let text = ev.clipboardData.getData("text/plain");
      text = text.replace(/[^0-9]/g, "");

      this.value = text;
    }

    get value() {
      return this.#otp;
    }
    set value(value) {
      if(typeof value == "number") {
        value = value.toString();
      } else if(typeof value !== "string") {
        value = ''
      }

      this.#setValue(value);

      const inputs = Array.from(this.shadowRoot?.querySelectorAll("input.digit") || []);
      inputs.forEach((input, i) => {
        input.value = value[i] || "";
      });
    }
    #setValue(value) {
      this.#otp = value;
      this.#internals.setFormValue(value);
      this.checkValidity();
    }
    get form() {
      return this.#internals.form;
    }
    get name() {
      return this.#fieldName;
    }
    get type() {
      return this.localName;
    }

    get required() {
      return this.getAttribute("required");
    }

    get size() {
      return this.getAttribute("size");
    }

    get autofocus() {
      return this.getAttribute("autofocus");
    } 

    get tabIndex() {
      return this.getAttribute("tabindex");
    }

    set tabIndex(value) {
      this.setAttribute("tabindex", value);
    }
  }
);
